import { useState } from 'react'
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import { ArrowPathIcon, ChevronDownIcon } from '@heroicons/react/20/solid'
import { AdManagementService, type CtaType_Input } from '../../client'
import { CTA_LABELS, type AdCreative } from '../../lib/adPromotion'
import MetaAdPreview from './MetaAdPreview'

type Field = 'primary_text' | 'headline' | 'description'

const LABEL = 'mb-1 flex items-center gap-1 text-xs font-medium text-gray-700'
const LEVEL = 'text-gray-400'
const INPUT =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none'

function Regenerate({ disabled, loading, onClick }: { disabled: boolean; loading: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      title="Regenerate"
      disabled={disabled || loading}
      onClick={onClick}
      className="ml-1 rounded p-0.5 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
    >
      <ArrowPathIcon className={`size-3.5 text-gray-400 ${loading ? 'animate-spin text-indigo-500' : ''}`} />
    </button>
  )
}

type Props = {
  brandId: number
  creative: AdCreative
  imageUrl?: string
  brandName?: string
  brandWebsite?: string
  brandDescription?: string
  regenerated: Set<Field>
  onChange: (creative: AdCreative) => void
  onRegenerated: (field: Field) => void
}

export default function AdCreativeConfig({
  brandId,
  creative,
  imageUrl,
  brandName,
  brandWebsite,
  brandDescription,
  regenerated,
  onChange,
  onRegenerated,
}: Props) {
  const [regenerating, setRegenerating] = useState<Field | null>(null)

  function update(patch: Partial<AdCreative>) {
    onChange({ ...creative, ...patch })
  }

  async function regenerate(field: Field) {
    setRegenerating(field)
    try {
      const { value } = await AdManagementService.regenerateField({
        brand_id: brandId,
        field,
        image_url: imageUrl ?? '',
        brand_name: brandName ?? '',
        brand_description: brandDescription,
        current_values: {
          primary_text: creative.primaryText,
          headline: creative.headline,
          description: creative.description,
        },
      })
      update(
        field === 'primary_text' ? { primaryText: value } : field === 'headline' ? { headline: value } : { description: value },
      )
      onRegenerated(field)
    } catch {
      // a failed suggestion leaves the field as the user had it
    } finally {
      setRegenerating(null)
    }
  }

  return (
    <div className="grid gap-4 md:grid-cols-[300px_1fr]">
      <div className="relative hidden overflow-hidden rounded-xl bg-gray-100 md:block">
        <div className="absolute inset-0">
          <MetaAdPreview creative={creative} imageUrl={imageUrl} brandName={brandName} brandWebsite={brandWebsite} />
        </div>
      </div>

      <div className="space-y-2 pr-1 pl-0.5">
        <div>
          <label className={LABEL}>
            Ad name <span className={LEVEL}>[Ad level]</span>
          </label>
          <input
            type="text"
            value={creative.adName}
            onChange={(event) => update({ adName: event.target.value })}
            className={INPUT}
          />
        </div>

        <div>
          <label className={LABEL}>
            Primary text <span className={LEVEL}>[Creative level]</span> <span className={LEVEL}>(optional)</span>
            {brandName && (
              <Regenerate
                disabled={regenerated.has('primary_text')}
                loading={regenerating === 'primary_text'}
                onClick={() => void regenerate('primary_text')}
              />
            )}
          </label>
          <textarea
            rows={5}
            value={creative.primaryText}
            placeholder="The main message of your ad"
            onChange={(event) => update({ primaryText: event.target.value })}
            className={INPUT}
          />
        </div>

        <div className="grid grid-cols-[2fr_1fr] gap-3">
          <div>
            <label className={LABEL}>
              Headline <span className={LEVEL}>[Creative level]</span> <span className={LEVEL}>(optional)</span>
              {brandName && (
                <Regenerate
                  disabled={regenerated.has('headline')}
                  loading={regenerating === 'headline'}
                  onClick={() => void regenerate('headline')}
                />
              )}
            </label>
            <input
              type="text"
              value={creative.headline}
              placeholder="Short headline"
              onChange={(event) => update({ headline: event.target.value })}
              className={INPUT}
            />
          </div>

          <div>
            <label className={LABEL}>
              CTA <span className={LEVEL}>[Creative level]</span>
            </label>
            <Listbox value={creative.ctaType} onChange={(ctaType: CtaType_Input) => update({ ctaType })}>
              <ListboxButton className="group flex w-full min-w-0 items-center justify-between rounded-lg border border-gray-300 bg-white px-3 py-2 text-left text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 focus:outline-none">
                <span className="truncate text-gray-800">{CTA_LABELS[creative.ctaType]}</span>
                <ChevronDownIcon className="ml-2 size-4 shrink-0 text-gray-400 transition-transform group-data-open:rotate-180" />
              </ListboxButton>
              <ListboxOptions
                anchor="bottom start"
                className="z-[60] w-(--button-width) divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg [--anchor-gap:4px] [--anchor-max-height:10rem]"
              >
                {Object.entries(CTA_LABELS).map(([value, label]) => (
                  <ListboxOption
                    key={value}
                    value={value}
                    className="cursor-pointer px-3 py-1.5 text-sm text-gray-800 transition hover:bg-gray-50 data-selected:bg-indigo-50 data-selected:text-indigo-700"
                  >
                    {label}
                  </ListboxOption>
                ))}
              </ListboxOptions>
            </Listbox>
          </div>
        </div>

        <div>
          <label className={LABEL}>
            Description <span className={LEVEL}>[Creative level]</span> <span className={LEVEL}>(optional)</span>
            {brandName && (
              <Regenerate
                disabled={regenerated.has('description')}
                loading={regenerating === 'description'}
                onClick={() => void regenerate('description')}
              />
            )}
          </label>
          <textarea
            rows={5}
            value={creative.description}
            placeholder="Additional description"
            onChange={(event) => update({ description: event.target.value })}
            className={INPUT}
          />
        </div>

        <div>
          <label className={LABEL}>
            Destination <span className={LEVEL}>[Creative level]</span> <span className={LEVEL}>(optional)</span>
          </label>
          <input
            type="url"
            value={creative.ctaLink}
            placeholder="https://yourbrand.com"
            onChange={(event) => update({ ctaLink: event.target.value })}
            className={INPUT}
          />
        </div>
      </div>
    </div>
  )
}
